// Smart Sash Admin Panel - Report Service
// Hood usage report data with fake data generation (swappable with real API)

import {
  HoodUsageFilters,
  HoodUsageQueryParams,
  HoodUsageSession,
  HoodUsageSortField,
  HoodUsageSortState,
  PaginatedResponse,
} from '@/models';
import {
  useGroupStore,
  useDeviceStore,
  useInstitutionStore,
  useLabStore,
  useUserStore,
} from '@/store';
import { generateFakeSessions } from './fakeHoodData';

let cachedSessions: HoodUsageSession[] | null = null;

function getOrGenerateSessions(): HoodUsageSession[] {
  if (cachedSessions) return cachedSessions;

  const institutions = useInstitutionStore.getState().institutions;
  const groups = useGroupStore.getState().groups;
  const labs = useLabStore.getState().labs;
  const users = useUserStore.getState().users;
  const devices = useDeviceStore.getState().devices;

  cachedSessions = generateFakeSessions(200, institutions, groups, labs, users, devices);
  return cachedSessions;
}

function applyFilters(
  sessions: HoodUsageSession[],
  filters: HoodUsageFilters
): HoodUsageSession[] {
  return sessions.filter((s) => {
    if (filters.institutionIds.length > 0 && !filters.institutionIds.includes(s.institutionId)) {
      return false;
    }
    if (filters.groupIds.length > 0 && !filters.groupIds.includes(s.groupId)) {
      return false;
    }
    if (filters.labIds.length > 0 && !filters.labIds.includes(s.labId)) {
      return false;
    }
    if (filters.userIds.length > 0 && !filters.userIds.includes(s.userId)) {
      return false;
    }
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom).getTime();
      if (new Date(s.sessionDate).getTime() < from) return false;
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo).getTime() + 24 * 60 * 60 * 1000;
      if (new Date(s.sessionDate).getTime() > to) return false;
    }
    return true;
  });
}

function applySort(
  sessions: HoodUsageSession[],
  sort: HoodUsageSortState
): HoodUsageSession[] {
  const sorted = [...sessions];
  const dir = sort.order === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    const field = sort.field as HoodUsageSortField;
    const aVal = a[field];
    const bVal = b[field];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * dir;
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * dir;
    }

    return 0;
  });

  return sorted;
}

function applyPagination(
  sessions: HoodUsageSession[],
  page: number,
  pageSize: number
): PaginatedResponse<HoodUsageSession> {
  const totalCount = sessions.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const data = sessions.slice(start, start + pageSize);

  return {
    data,
    pagination: {
      currentPage: safePage,
      pageSize,
      totalPages,
      totalCount,
      hasNext: safePage < totalPages,
      hasPrevious: safePage > 1,
    },
  };
}

export const reportService = {
  getHoodUsageSessions: async (
    params: HoodUsageQueryParams
  ): Promise<PaginatedResponse<HoodUsageSession>> => {
    const all = getOrGenerateSessions();
    const filtered = applyFilters(all, params.filters);
    const sorted = applySort(filtered, params.sort);
    return applyPagination(sorted, params.page, params.pageSize);
  },

  /** Clear cached fake data (useful for testing or when entities are reloaded) */
  resetCache: () => {
    cachedSessions = null;
  },
};

export default reportService;
